import React, { useState, useEffect } from 'react';
import apiService, { getImageUrl } from '../../services/api';

const GalleryOne = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalTitle, setModalTitle] = useState('');

  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        setLoading(true);
        const response = await apiService.getFeaturedGalleryImages();
        console.log('GalleryOne: API response:', response);
        
        // Map API data to component format
        const mappedImages = response.results?.map(image => ({
          id: image.id,
          title: image.title,
          description: image.description,
          imgSrc: image.image ? getImageUrl(image.image) : `/assets/img/gallery/gallery_1_${(image.id % 7) + 1}.jpg`,
          featured: image.featured,
          order: image.order
        })) || [];

        console.log('GalleryOne: Mapped gallery images:', mappedImages);
        
        // Sort by order, then by id
        const sortedImages = mappedImages.sort((a, b) => {
          if (a.order === b.order) {
            return a.id - b.id;
          }
          return a.order - b.order;
        });

        setGalleryImages(sortedImages);
        setError(null);
      } catch (err) {
        console.error('GalleryOne: Error fetching gallery images:', err);
        setError('Failed to load gallery images');
        
        // Fallback to static images if API fails
        const fallbackImages = [
          { id: 1, title: "Beautiful Destination", imgSrc: "/assets/img/gallery/gallery_1_1.jpg", description: "Amazing view", order: 1 },
          { id: 2, title: "Adventure Tour", imgSrc: "/assets/img/gallery/gallery_1_2.jpg", description: "Exciting adventure", order: 2 },
          { id: 3, title: "Cultural Experience", imgSrc: "/assets/img/gallery/gallery_1_3.jpg", description: "Rich culture", order: 3 },
          { id: 4, title: "Wildlife Safari", imgSrc: "/assets/img/gallery/gallery_1_4.jpg", description: "Amazing wildlife", order: 4 },
          { id: 5, title: "Beach Paradise", imgSrc: "/assets/img/gallery/gallery_1_5.jpg", description: "Crystal clear waters", order: 5 },
          { id: 6, title: "Mountain View", imgSrc: "/assets/img/gallery/gallery_1_6.jpg", description: "Breathtaking mountains", order: 6 },
          { id: 7, title: "City Lights", imgSrc: "/assets/img/gallery/gallery_1_7.jpg", description: "Urban adventure", order: 7 }
        ];
        setGalleryImages(fallbackImages);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);

  const openModal = (image) => {
    setSelectedImage(image.imgSrc);
    setModalTitle(image.title);
    setModalOpen(true);
    document.body.classList.add('modal-open');
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
    setModalTitle('');
    document.body.classList.remove('modal-open');
  };

  // Ensure we have at least 7 images for the layout, duplicate if necessary
  const getDisplayImages = () => {
    if (galleryImages.length === 0) return [];
    
    if (galleryImages.length < 7) {
      const neededImages = [];
      for (let i = 0; i < 7; i++) {
        const sourceImage = galleryImages[i % galleryImages.length];
        neededImages.push({
          ...sourceImage,
          id: `${sourceImage.id}-dup-${i}`,
          title: sourceImage.title + (i >= galleryImages.length ? ` (${Math.floor(i / galleryImages.length) + 1})` : '')
        });
      }
      return neededImages;
    }
    
    return galleryImages.slice(0, 7); // Take only first 7 images
  };

  const displayImages = getDisplayImages();

  if (loading) {
    return (
      <section className="gallery-area">
        <div className="container">
          <div className="title-area text-center">
            <span className="sub-title">Make Your Tour More Pleasure</span>
            <h2 className="sec-title">Recent Gallery</h2>
          </div>
          <div className="row gy-10 gx-10 justify-content-center align-items-center">
            <div className="col-12 text-center">
              <p>Loading gallery images...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="gallery-area">
        <div className="container">
          <div className="title-area text-center">
            <span className="sub-title">Make Your Tour More Pleasure</span>
            <h2 className="sec-title">Recent Gallery</h2>
          </div>
          
          {displayImages.length > 0 ? (
            <div className="row gy-10 gx-10 justify-content-center align-items-center">
              {/* First Column - Single image */}
              <div className="col-md-6 col-lg-2">
                <div className="gallery-card">
                  <div className="box-img global-img">
                    <a 
                      className="popup-image" 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openModal(displayImages[0]);
                      }}
                      data-discover="true"
                    >
                      <div className="icon-btn">
                        <i className="fal fa-magnifying-glass-plus"></i>
                      </div>
                    </a>
                    <img src={displayImages[0]?.imgSrc} alt={displayImages[0]?.title || "gallery"} />
                  </div>
                </div>
              </div>

              {/* Second Column - Two images stacked */}
              <div className="col-md-6 col-lg-2">
                <div className="gallery-card">
                  <div className="box-img global-img">
                    <a 
                      className="popup-image" 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openModal(displayImages[1]);
                      }}
                      data-discover="true"
                    >
                      <div className="icon-btn">
                        <i className="fal fa-magnifying-glass-plus"></i>
                      </div>
                    </a>
                    <img src={displayImages[1]?.imgSrc} alt={displayImages[1]?.title || "gallery"} />
                  </div>
                </div>
                <div className="gallery-card">
                  <div className="box-img global-img">
                    <a 
                      className="popup-image" 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openModal(displayImages[2]);
                      }}
                      data-discover="true"
                    >
                      <div className="icon-btn">
                        <i className="fal fa-magnifying-glass-plus"></i>
                      </div>
                    </a>
                    <img src={displayImages[2]?.imgSrc} alt={displayImages[2]?.title || "gallery"} />
                  </div>
                </div>
              </div>

              {/* Third Column - Single image */}
              <div className="col-md-6 col-lg-2">
                <div className="gallery-card">
                  <div className="box-img global-img">
                    <a 
                      className="popup-image" 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openModal(displayImages[3]);
                      }}
                      data-discover="true"
                    >
                      <div className="icon-btn">
                        <i className="fal fa-magnifying-glass-plus"></i>
                      </div>
                    </a>
                    <img src={displayImages[3]?.imgSrc} alt={displayImages[3]?.title || "gallery"} />
                  </div>
                </div>
              </div>

              {/* Fourth Column - Two images stacked */}
              <div className="col-md-6 col-lg-2">
                <div className="gallery-card">
                  <div className="box-img global-img">
                    <a 
                      className="popup-image" 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openModal(displayImages[4]);
                      }}
                      data-discover="true"
                    >
                      <div className="icon-btn">
                        <i className="fal fa-magnifying-glass-plus"></i>
                      </div>
                    </a>
                    <img src={displayImages[4]?.imgSrc} alt={displayImages[4]?.title || "gallery"} />
                  </div>
                </div>
                <div className="gallery-card">
                  <div className="box-img global-img">
                    <a 
                      className="popup-image" 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openModal(displayImages[5]);
                      }}
                      data-discover="true"
                    >
                      <div className="icon-btn">
                        <i className="fal fa-magnifying-glass-plus"></i>
                      </div>
                    </a>
                    <img src={displayImages[5]?.imgSrc} alt={displayImages[5]?.title || "gallery"} />
                  </div>
                </div>
              </div>

              {/* Fifth Column - Single image */}
              <div className="col-md-6 col-lg-2">
                <div className="gallery-card">
                  <div className="box-img global-img">
                    <a 
                      className="popup-image" 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openModal(displayImages[6]);
                      }}
                      data-discover="true"
                    >
                      <div className="icon-btn">
                        <i className="fal fa-magnifying-glass-plus"></i>
                      </div>
                    </a>
                    <img src={displayImages[6]?.imgSrc} alt={displayImages[6]?.title || "gallery"} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="row gy-10 gx-10 justify-content-center align-items-center">
              <div className="col-12 text-center">
                <p>{error || 'No gallery images available'}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Modal for image popup */}
      {modalOpen && (
        <div 
          className="modal fade show" 
          style={{ display: 'block' }} 
          onClick={closeModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content bg-transparent border-0">
              <div className="modal-header border-0">
                <h5 className="modal-title text-white">{modalTitle}</h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body text-center">
                <img 
                  src={selectedImage} 
                  alt={modalTitle}
                  className="img-fluid rounded"
                  style={{ maxHeight: '80vh', objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal backdrop */}
      {modalOpen && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default GalleryOne;
